import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon.js';
import React from 'react';
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  type OverlayTriggerState,
  OverlayTriggerStateContext,
  Popover as AriaPopover,
} from 'react-aria-components';

import { tv } from '../../tv';
import { Heading } from '../heading';
import { IconButton } from '../icon-button';
import { OverlayPortal, surfacedOverlay, useOverlayPresence } from '../overlay/overlay-portal';
import { OVERLAY_OFFSET, RAC_PLACEMENT } from '../overlay/placement';
import { Header } from '../slots';
import { type SlotPadding, SlotStyleContext } from '../slots/slot-context';

/**
 * A small surface anchored to the control that opened it, for work that
 * belongs next to that control: a settings panel, a filter. The third
 * surfaced overlay after Menu and Toast (overlays.md): it wears the same
 * surface and lives in the same kind of portal, and it is always a modal
 * dialog, so focus moves in, stays in, and returns to the trigger.
 *
 * The body is composed from the shared slots (Content, Footer), exactly as
 * in a Card; the popover sets their inset. A popover opened by something
 * other than a press on its own control (a result arriving, a menu item)
 * anchors to an existing element instead (popover.md).
 */

const popoverStyles = tv({
  slots: {
    /* A column capped by React Aria's inline max-height, so a scrollable
     * Content slot takes the overflow instead of the popup growing. */
    popup: [...surfacedOverlay, 'ck:flex ck:max-w-full ck:flex-col ck:overflow-hidden'],
    dialog: 'ck:flex ck:min-h-0 ck:flex-1 ck:flex-col ck:outline-none',
    titleRow: 'ck:flex ck:items-center ck:justify-between ck:gap-2',
  },
  variants: {
    /* The component's own sm/md/lg ramp over the container scale, one step down:
     * sm is w-xs (320px), md is w-sm (384px), lg is w-md (448px). */
    size: {
      sm: { popup: 'ck:w-xs' },
      md: { popup: 'ck:w-sm' },
      lg: { popup: 'ck:w-md' },
    },
  },
  defaultVariants: { size: 'sm' },
});

interface PopoverBaseProps {
  /** Where the popover sits relative to the trigger; it flips when there is no room. */
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end';
  /** 320, 384 or 448px wide, the container scale. */
  size?: 'sm' | 'md' | 'lg';
  /** The inset the slots take, as on Card. */
  padding?: SlotPadding;
  /** The body: the shared Content and Footer slots. */
  children: React.ReactNode;
}

/* A popover either owns the control that opens it, or is opened by the
 * caller and anchored to an element that already exists. */
type PopoverOpeningProps =
  | {
      /**
       * The one ComplyKit interactive element that opens the popover, usually a
       * Button or an IconButton. Named as a prop, as Menu names its trigger, and
       * seated via context, so it needs no wiring.
       */
      trigger: React.ReactElement;
      /** Not with a trigger: the trigger is the anchor. */
      anchorRef?: never;
      /** Whether the popover is open (controlled). */
      isOpen?: boolean;
      /** Whether the popover is open when first rendered (uncontrolled). */
      defaultOpen?: boolean;
      /** Handler called when the popover opens or closes. */
      onOpenChange?: (isOpen: boolean) => void;
    }
  | {
      /**
       * The element the popover sits against, when nothing is pressed to open it:
       * a result arriving for a control, or a menu item acting for the menu's
       * button. The anchor is not wired as a trigger, so it gains no pressed or
       * expanded state; focus returns to wherever it was when the popover opened.
       */
      anchorRef: React.RefObject<Element | null>;
      /** Not with an anchor: an anchored popover is opened by the caller. */
      trigger?: never;
      /** Whether the popover is open; an anchored popover is always controlled. */
      isOpen: boolean;
      /** Not with an anchor: an anchored popover is always controlled. */
      defaultOpen?: never;
      /** Handler called when the popover asks to close (Escape, a press outside, the close control). */
      onOpenChange: (isOpen: boolean) => void;
    };

/* Every popover is a dialog, and a dialog needs a name: a visible title that
 * names it (with a close control, whose name the caller supplies), or a
 * label for assistive technology alone. */
type PopoverNamingProps =
  | {
      /** The visible heading that names the dialog; renders the header row with a close control. */
      title: string;
      /** The close control's accessible name, supplied by the caller since ComplyKit carries no copy. */
      closeLabel: string;
      /** Not with a title: the title already names the dialog. */
      label?: never;
    }
  | {
      /** Names the dialog for assistive technology alone: no header and no close control. */
      label: string;
      /** Not with a label: a label-named popover has no heading. */
      title?: never;
      /** Not with a label: without a heading there is no close control to name. */
      closeLabel?: never;
    };

export type PopoverProps = PopoverBaseProps & PopoverOpeningProps & PopoverNamingProps;

function PopoverTitle({
  id,
  title,
  closeLabel,
}: {
  id: string;
  title: string;
  closeLabel: string;
}) {
  const state = React.useContext(OverlayTriggerStateContext);
  const { titleRow } = popoverStyles();
  return (
    <Header>
      <div className={titleRow()}>
        <Heading as="h2" size="xs" id={id} isTruncated>
          {title}
        </Heading>
        <IconButton
          icon={XMarkIcon}
          label={closeLabel}
          variant="ghost"
          size="sm"
          tooltip={false}
          onPress={() => state?.close()}
        />
      </div>
    </Header>
  );
}

const PopoverPopup = React.forwardRef<
  HTMLDivElement,
  PopoverNamingProps &
    Required<Pick<PopoverBaseProps, 'placement' | 'size' | 'padding'>> &
    Pick<PopoverBaseProps, 'children'> & { anchorRef?: React.RefObject<Element | null> }
>(function PopoverPopup(
  { placement, size, padding, title, closeLabel, label, anchorRef, children },
  forwardedRef,
) {
  const state = React.useContext(OverlayTriggerStateContext);
  const { popupRef, isPresent } = useOverlayPresence(state?.isOpen, forwardedRef);
  const titleId = React.useId();
  const slotStyle = React.useMemo(() => ({ padding }), [padding]);
  const { popup, dialog } = popoverStyles({ size });
  if (!isPresent) return null;

  return (
    <OverlayPortal name="popover">
      <AriaPopover
        ref={popupRef}
        triggerRef={anchorRef}
        placement={RAC_PLACEMENT[placement]}
        offset={OVERLAY_OFFSET}
        className={popup()}
        style={{ zIndex: 'var(--ck-z-overlay)' } as React.CSSProperties}
        data-component="popover"
      >
        <AriaDialog
          className={dialog()}
          aria-label={label}
          aria-labelledby={title === undefined ? undefined : titleId}
        >
          <SlotStyleContext.Provider value={slotStyle}>
            {title !== undefined && closeLabel !== undefined ? (
              <PopoverTitle id={titleId} title={title} closeLabel={closeLabel} />
            ) : null}
            {children}
          </SlotStyleContext.Provider>
        </AriaDialog>
      </AriaPopover>
    </OverlayPortal>
  );
});

/* The state a DialogTrigger would provide, built from the caller's control,
 * so the popup reads one context whichever way it was opened. */
function AnchoredState({
  isOpen,
  onOpenChange,
  children,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  children: React.ReactNode;
}) {
  const state = React.useMemo<OverlayTriggerState>(
    () => ({
      isOpen,
      setOpen: onOpenChange,
      open: () => onOpenChange(true),
      close: () => onOpenChange(false),
      toggle: () => onOpenChange(!isOpen),
    }),
    [isOpen, onOpenChange],
  );
  return (
    <OverlayTriggerStateContext.Provider value={state}>
      {children}
    </OverlayTriggerStateContext.Provider>
  );
}

export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(function Popover(
  { placement = 'bottom-start', size = 'sm', padding = 'default', ...props },
  ref,
) {
  if (props.anchorRef !== undefined) {
    const { anchorRef, isOpen, onOpenChange, ...popup } = props;
    return (
      <AnchoredState isOpen={isOpen} onOpenChange={onOpenChange}>
        <PopoverPopup
          ref={ref}
          anchorRef={anchorRef}
          placement={placement}
          size={size}
          padding={padding}
          {...popup}
        />
      </AnchoredState>
    );
  }
  const { trigger, isOpen, defaultOpen, onOpenChange, ...popup } = props;
  return (
    <AriaDialogTrigger isOpen={isOpen} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger}
      <PopoverPopup ref={ref} placement={placement} size={size} padding={padding} {...popup} />
    </AriaDialogTrigger>
  );
});
